import { 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  updateProfile
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { DEFAULT_IMAGES } from '../constants/defaults';

/**
 * Iniciar sesión con email y contraseña
 */
export const login = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return { success: true, user: userCredential.user };
  } catch (error) {
    console.error('Error al iniciar sesión:', error);
    return { 
      success: false, 
      error: getErrorMessage(error.code) 
    };
  }
};

/**
 * Registrar nuevo usuario
 */
export const register = async (email, password, displayName) => {
  try {
    // Crear usuario en Firebase Auth
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Actualizar perfil con nombre y foto por defecto
    await updateProfile(user, {
      displayName: displayName,
      photoURL: DEFAULT_IMAGES.PROFILE
    });

    // Crear documento en Firestore
    await setDoc(doc(db, 'users', user.uid), {
      uid: user.uid,
      email: email,
      displayName: displayName,
      photoURL: DEFAULT_IMAGES.PROFILE,
      bannerURL: DEFAULT_IMAGES.BANNER,
      bio: '',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      // Campos adicionales básicos
      isCreator: false,
      isPremium: false,
      followers: [],
      following: [],
      postsCount: 0,
      followersCount: 0,
      followingCount: 0
    });

    return { success: true, user };
  } catch (error) {
    console.error('Error al registrar usuario:', error);
    return { 
      success: false, 
      error: getErrorMessage(error.code) 
    };
  }
};

/**
 * Cerrar sesión
 */
export const logout = async () => {
  try {
    await firebaseSignOut(auth);
    return { success: true };
  } catch (error) {
    console.error('Error al cerrar sesión:', error);
    return { 
      success: false, 
      error: 'Error al cerrar sesión' 
    };
  }
};

/**
 * Enviar email de recuperación de contraseña
 */
export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return { success: true };
  } catch (error) {
    console.error('Error al enviar email de recuperación:', error);
    return { 
      success: false, 
      error: getErrorMessage(error.code) 
    };
  }
};

/**
 * Obtener datos del usuario desde Firestore
 */
export const getUserData = async (uid) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (userDoc.exists()) {
      return { success: true, data: userDoc.data() };
    } else {
      return { success: false, error: 'Usuario no encontrado' };
    }
  } catch (error) {
    console.error('Error al obtener datos del usuario:', error);
    return { success: false, error: 'Error al obtener datos' };
  }
};

/**
 * Mapear códigos de error de Firebase a mensajes amigables
 */
const getErrorMessage = (errorCode) => {
  const errorMessages = {
    'auth/invalid-email': 'El email no es válido',
    'auth/user-disabled': 'Esta cuenta ha sido deshabilitada',
    'auth/user-not-found': 'No existe una cuenta con este email',
    'auth/wrong-password': 'Contraseña incorrecta',
    'auth/email-already-in-use': 'Este email ya está registrado',
    'auth/weak-password': 'La contraseña debe tener al menos 6 caracteres',
    'auth/network-request-failed': 'Error de conexión. Verifica tu internet',
    'auth/too-many-requests': 'Demasiados intentos. Intenta más tarde',
    'auth/invalid-credential': 'Credenciales inválidas'
  };

  return errorMessages[errorCode] || 'Error desconocido. Intenta nuevamente';
};
